import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shirt, PlusCircle, FilterIcon, ArrowLeft, Building2 } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import DynamicFormModal from '@/components/DynamicFormModal';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { clientGarmentsColumns, columnsExcel } from './utils/clientGarmentsColumns';
import { createClientGarment, getClientGarments, updateClientGarment, deleteClientGarment, exportClientGarments, updateAssignedGarmentsFromBase } from './Services/clientGarments.services';

const ClientGarmentsPage = () => {
  const [searchParams] = useSearchParams();
  const idLavanderia = searchParams.get('lavanderia');
  const [laundryName, setLaundryName] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(
    idLavanderia ? { query: { 'lavanderia.id': idLavanderia }, page: 1, limit: 10 } : { query: {}, page: 1, limit: 10 }
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { toast } = useToast();

  const buildBackendQuery = (rawQuery) => {
    const query = { ...rawQuery };
    if (query.nombre && typeof query.nombre === 'string') {
      const value = query.nombre.trim();
      if (value) {
        query.nombre = { $regex: value, $options: 'i' };
      } else {
        delete query.nombre;
      }
    }
    return query;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getClientGarments({
        ...filters,
        query: buildBackendQuery(filters.query),
      });
      setData(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las prendas de clientes.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch laundry name if coming from CentroParametrizacionPage
  useEffect(() => {
    if (idLavanderia) {
      const fetchLaundryName = async () => {
        try {
          const { fetchLaundryById } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
          const laundry = await fetchLaundryById(idLavanderia);
          setLaundryName(laundry?.nombre || '');
        } catch (e) {
          console.error('Error fetching laundry:', e);
        }
      };
      fetchLaundryName();
    }
  }, [idLavanderia]);

  const handleFilter = (newFilters) => {
    setFilters((prev) => ({ ...prev, query: newFilters, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({ ...prev, query: {}, page: 1 }));
  };

  const handleRemoveFilter = (filterKey) => {
    setFilters((prev) => {
      const newQuery = { ...prev.query };
      delete newQuery[filterKey];
      return { ...prev, query: newQuery, page: 1 };
    });
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await updateClientGarment(editingItem._id, formData);

        // Actualizar las asignaciones de prendas a clientes con los nuevos datos
        try {
          await updateAssignedGarmentsFromBase(editingItem._id, {
            nombre: formData.nombre,
            peso: formData.peso,
            areaAlistamiento: formData.areaAlistamiento,
          });
        } catch (assignmentError) {
          console.warn('No se pudieron actualizar las asignaciones:', assignmentError);
        }

        toast({ title: 'Éxito', description: 'Prenda actualizada correctamente.' });
      } else {
        await createClientGarment(formData);
        toast({ title: 'Éxito', description: 'Prenda creada correctamente.' });
      }
      fetchData();
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: editingItem ? 'No se pudo actualizar la prenda.' : 'No se pudo crear la prenda.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleAction = (actionType, row) => {
    if (actionType === 'edit') {
      handleEdit(row);
    } else if (actionType === 'delete') {
      handleDelete(row);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteClientGarment(itemToDelete._id);
      toast({ title: 'Éxito', description: 'Prenda eliminada correctamente.' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la prenda.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleExport = async () => {
    try {
      await exportClientGarments(buildBackendQuery(filters.query));
      toast({ title: 'Éxito', description: 'Datos exportados correctamente.' });
    } catch (error) {
      console.error('Error al exportar:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron exportar los datos.',
        variant: 'destructive',
      });
    }
  };

  // Filtrar columnas para el formulario (excluir actions)
  const formFields = clientGarmentsColumns
    .filter(col => col.id !== 'actions')
    .map(col => ({
      ...col,
      name: col.id,
      placeholder: col.id === 'nombre' ? 'Ej: Sábana matrimonial' :
        col.id === 'peso' ? 'Ej: 500' : undefined,
      defaultValue: col.id === 'estado' ? 'Activo' : undefined,
      min: col.id === 'peso' ? 0 : undefined,
    }));

  const filterFields = [
    {
      id: 'nombre',
      name: 'nombre',
      label: 'Nombre',
      type: 'text',
      placeholder: 'Buscar por nombre...',
    },
    {
      name: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: '', label: 'Todos' },
        { value: 'Activo', label: 'Activo' },
        { value: 'Inactivo', label: 'Inactivo' },
      ],
    },
  ];

  return (
    <div className="container mx-auto ">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              {idLavanderia && (
                <Button asChild variant="outline" size="icon" className="shrink-0">
                  <Link to={`/parametrizacion/centros/${idLavanderia}`}>
                    <ArrowLeft className="h-5 w-5" />
                  </Link>
                </Button>
              )}
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shirt className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-2xl">
                  Prendas de clientes internos
                  {laundryName && (
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      — {laundryName}
                    </span>
                  )}
                </CardTitle>
                <CardDescription>
                  {idLavanderia
                    ? 'Gestiona las prendas de clientes del centro de lavado.'
                    : 'Gestiona las prendas de clientes para el de alistamiento y entrega'}
                </CardDescription>
              </div>
            </div>
              <div className="flex flex-wrap gap-2">
                <ExportExcelButton
                  data={data}
                  columns={columnsExcel}
                  fileName="prendas_clientes"
                  onExport={handleExport}
                />

                <Button
                  onClick={() => {
                    setEditingItem(null);
                    setIsModalOpen(true);
                  }}
                  className="gap-2"
                >
                  <PlusCircle className="h-4 w-4" />
                  Nueva Prenda
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsFilterOpen(true)}
                  className="gap-2"
                >
                  <FilterIcon className="h-4 w-4" />
                  Filtros
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <AppliedFilters
              filters={filters.query}
              fields={filterFields}
              onClearFilters={handleClearFilters}
              onRemoveFilter={handleRemoveFilter}
            />
            <DataTable
              columns={clientGarmentsColumns}
              data={data}
              isLoading={loading}
              onAction={handleAction}
              page={filters.page}
              limit={filters.limit}
              totalRecords={totalRecords}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          </CardContent>
        </Card>
      </motion.div>

      <DynamicFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        item={editingItem}
        fields={formFields}
        title={editingItem ? 'Editar Prenda' : 'Nueva Prenda'}
      />

      <FilterDrawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilters={handleFilter}
        onApply={handleFilter}
        fields={filterFields}
        currentFilters={filters.query}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la prenda "{itemToDelete?.nombre}". Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientGarmentsPage;
